import { RJSFSchema } from '@rjsf/utils';

import {
  CANADA_POST_PROVINCE_CODES,
  FilingInstitutionNonTinIdentificationTypeCode,
  FilingInstitutionOrganizationSubtypeCodes,
  FillingInstitutionTinIdentificationTypeCode,
  ISO_3166_2_MX_STATE_CODES,
  ISO_3166_COUNTRY_CODES,
  MAILING_ADDRESS,
  NAICSCode,
  REQUIRED_MAILING_ADDRESS,
  TIN,
  USPS_STATE_CODES,
} from 'dashboard/views/DashboardSARShowView/FormSAR/constants';
import { HorizontalLayoutObjectFieldTemplate } from 'dashboard/views/DashboardSARShowView/FormSAR/rjsf/FormComponents/HorizontalLayoutObjectFieldTemplate';

interface SARFormSection {
  title: string;
  schema: RJSFSchema;
  uiSchema?: any; // TODO: type this with generics
}

type SARFormConfiguration = Record<string, SARFormSection>;

export const sarFormConfiguration: SARFormConfiguration = {
  general: {
    title: 'General',
    schema: {
      type: 'object',
      properties: {
        filingTypeDetails: {
          type: 'object',
          properties: {
            filingTypes: {
              type: 'array',
              title: 'Filing type',
              uniqueItems: true,
              minItems: 1,
              items: {
                type: 'string',
                oneOf: [
                  { const: 'initial_report', title: 'Initial report' },
                  { const: 'corrects_amends_prior_report', title: 'Correct/ Amend prior report' },
                  { const: 'continuing_activity_report', title: 'Continuing activity report' },
                  { const: 'joint_report', title: 'Joint report' },
                ],
              },
            },
          },
          required: ['filingTypes'],
          if: {
            $comment:
              'if filing type is continuing or correct/amend report, show prior report bsa id',
            properties: {
              filingTypes: {
                contains: {
                  anyOf: [
                    { const: 'corrects_amends_prior_report' },
                    { const: 'continuing_activity_report' },
                  ],
                },
              },
            },
            required: ['filingTypes'],
          },
          then: {
            properties: {
              priorDocumentNumber: {
                title: 'Prior report BSA identification number (BSA ID)',
                type: 'string',
                pattern: '^[0-9]*$',
                maxLength: 14,
              },
            },
            required: ['priorDocumentNumber'],
            // required for amend/ continuing activity reports, but optional for joint report ://
          },
        },
        note: {
          type: 'string',
          title: 'Filing institution note to FinCEN',
          maxLength: 50,
          pattern: '^(?!\\s).*(?<!\\s)$',
        },
      },
    },
  },
  filingInstitution: {
    title: 'Filing Institution',
    schema: {
      type: 'object',
      properties: {
        filingInstitutionContactInformation: {
          type: 'object',
          title: 'Filing Institution Contact Information',
          properties: {
            typeOfFinancialInstitution: {
              type: 'object',
              properties: {
                organizationTypeId: {
                  type: 'number',
                  title: 'Type of financial institution',
                  oneOf: [
                    { const: 1, title: 'Casino/Card Club' },
                    { const: 2, title: 'Depository Institution' },
                    { const: 3, title: 'Insurance company' },
                    { const: 4, title: 'MSB (Money Service Business)' },
                    { const: 5, title: 'Securities/Futures' },
                    { const: 11, title: 'Loan or Finance Company' },
                    { const: 12, title: 'Housing GSE (Government Sponsored Enterprise)' },
                    { const: 999, title: 'Other' },
                  ],
                },
              },
              required: ['organizationTypeId'],
              allOf: [
                {
                  if: {
                    $comment: 'if other is selected, show other text input',
                    properties: { organizationTypeId: { const: 999 } },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      otherOrganizationTypeText: {
                        type: 'string',
                        title: 'Please specify other',
                        pattern: '^(?!\\s).*(?<!\\s)$',
                      },
                    },
                    required: ['otherOrganizationTypeText'],
                  },
                },
                {
                  if: {
                    $comment: 'if securities/futures is selected show subtype selection',
                    properties: { organizationTypeId: { const: 5 } },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCodeSecuritiesAndFutures: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [
                          { const: 9, title: 'CFTC (Commodities Futures Trading Commission)' },
                          { const: 6, title: 'SEC (Securities and Exchange Commission)' },
                        ],
                      },
                      organizationSubtypeId: {
                        title: 'Type of Securities and Futures Institution',
                        description: 'Select all that apply',
                        type: 'array',
                        uniqueItems: true,
                        items: {
                          type: 'number',
                          oneOf: FilingInstitutionOrganizationSubtypeCodes,
                        },
                        default: [],
                      },
                    },
                    required: [
                      'organizationSubtypeId',
                      'primaryRegulatorTypeCodeSecuritiesAndFutures',
                    ],
                    if: {
                      $comment: 'if other is selected, show other text input',
                      properties: { organizationSubtypeId: { contains: { const: 5999 } } },
                      required: ['organizationSubtypeId'],
                    },
                    then: {
                      properties: {
                        otherOrganizationSubTypeText: {
                          title: 'Other',
                          type: 'string',
                          pattern: '^(?!\\s).*(?<!\\s)$',
                        },
                      },
                      required: ['otherOrganizationSubTypeText'],
                    },
                  },
                },
                {
                  if: {
                    $comment:
                      'if depository institution is selected, show related primary regulator type',
                    properties: {
                      organizationTypeId: { const: 2 },
                    },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCodeDepository: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [
                          { const: 2, title: 'FDIC (Federal Deposit Insurance Corporation)' },
                          { const: 13, title: 'FHFA (Federal Housing Finance Agency)' },
                          { const: 7, title: 'IRS (Internal Revenue Service)' },
                          { const: 3, title: 'NCUA (National Credit Union Administration)' },
                          { const: 4, title: 'OCC (Office of the Comptroller of the Currency)' },
                        ],
                      },
                    },
                    required: ['primaryRegulatorTypeCodeDepository'],
                  },
                },
                {
                  if: {
                    $comment:
                      'if casino/card club, insurance company or MSB is selected, only show IRS as option',
                    properties: {
                      organizationTypeId: { anyOf: [{ const: 1 }, { const: 3 }, { const: 4 }] },
                    },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCodeIRS: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [{ const: 7, title: 'IRS (Internal Revenue Service)' }],
                      },
                    },
                    required: ['primaryRegulatorTypeCodeIRS'],
                  },
                },
                {
                  if: {
                    properties: {
                      organizationTypeId: { anyOf: [{ const: 11 }, { const: 12 }, { const: 999 }] },
                    },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCode: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [
                          { const: 9, title: 'CFTC (Commodities Futures Trading Commission)' },
                          { const: 1, title: 'Federal Reserve Board (FRB)' },
                          { const: 2, title: 'FDIC (Federal Deposit Insurance Corporation)' },
                          { const: 13, title: 'FHFA (Federal Housing Finance Agency)' },
                          { const: 7, title: 'IRS (Internal Revenue Service)' },
                          { const: 3, title: 'NCUA (National Credit Union Administration)' },
                          { const: 4, title: 'OCC (Office of the Comptroller of the Currency)' },
                          { const: 6, title: 'SEC (Securities and Exchange Commission)' },
                          { const: 99, title: 'Not Applicable' },
                        ],
                      },
                    },
                    required: ['primaryRegulatorTypeCode'],
                  },
                },
              ],
            },
            filerName: {
              type: 'object',
              properties: {
                rawPartyFullName: {
                  title:
                    'Filer name (Holding company, lead financial institution, or agency, if applicable)',
                  type: 'string',
                  maxLength: 150,
                  pattern: '^(?!\\s).*(?<!\\s)$',
                },
              },
              required: ['rawPartyFullName'],
            },
            tin: {
              type: 'object',
              properties: {
                partyIdentificationTypeCode: {
                  title: 'TIN Type',
                  type: 'number',
                  oneOf: FillingInstitutionTinIdentificationTypeCode,
                },
              },
              required: ['partyIdentificationTypeCode'],
              allOf: [
                {
                  if: {
                    properties: {
                      partyIdentificationTypeCode: { oneOf: [{ const: 1 }, { const: 2 }] },
                    },
                    required: ['partyIdentificationTypeCode'],
                  },
                  then: {
                    properties: {
                      partyIdentificationNumber: {
                        title: 'TIN',
                        type: 'string',
                        maxLength: 9,
                        minLength: 9,
                        pattern: '^[0-9]*$',
                      },
                    },
                    required: ['partyIdentificationNumber'],
                  },
                },
                {
                  if: {
                    properties: {
                      partyIdentificationTypeCode: { oneOf: [{ const: 9 }] },
                    },
                    required: ['partyIdentificationTypeCode'],
                  },
                  then: {
                    properties: {
                      partyIdentificationNumber: {
                        title: 'TIN',
                        type: 'string',
                        maxLength: 25,
                        pattern: '^[A-Za-z0-9]*$',
                      },
                    },
                    required: ['partyIdentificationNumber'],
                  },
                },
              ],
            },
            financialInstitutionIdentification: {
              title: 'Financial Institution Identification',
              type: 'object',
              properties: {
                partyIdentificationTypeCode: {
                  title: 'Type',
                  type: 'number',
                  oneOf: FilingInstitutionNonTinIdentificationTypeCode,
                },
                partyIdentificationNumberText: {
                  title: 'Number',
                  type: 'string',
                  maxLength: 10,
                  pattern: '^[A-Za-z0-9]*$',
                },
              },
              if: {
                properties: {
                  partyIdentificationTypeCode: {
                    oneOf: FilingInstitutionNonTinIdentificationTypeCode,
                  },
                },
                required: ['partyIdentificationTypeCode'],
              },
              then: {
                required: ['partyIdentificationNumberText'],
              },
            },
            contactOffice: {
              type: 'object',
              properties: {
                rawPartyFullName: {
                  title: 'Contact office name',
                  type: 'string',
                  maxLength: 150,
                  pattern: '^(?!\\s).*(?<!\\s)$',
                },
              },
              required: ['rawPartyFullName'],
            },
            contactPhoneNumber: {
              type: 'object',
              properties: {
                phoneNumberText: {
                  title: 'Contact phone number (include area code)',
                  type: 'string',
                  maxLength: 16,
                  // skipped not all same digit validation
                  pattern: '^[0-9]*$',
                },
                phoneNumberExtensionText: {
                  title: 'Ext.',
                  type: 'string',
                  maxLength: 6,
                  pattern: '^[0-9]*$',
                },
              },
              required: ['phoneNumberText'],
            },
            mailingAddress: REQUIRED_MAILING_ADDRESS,
            alternateName: {
              properties: {
                rawPartyFullName: {
                  title: 'Alternate name',
                  type: 'string',
                  maxLength: 150,
                  pattern: '^(?!\\s).*(?<!\\s)$',
                },
              },
            },
            internalControlNumber: {
              properties: {
                partyIdentificationNumberText: {
                  title: 'Internal control number',
                  type: 'string',
                  maxLength: 20,
                  pattern: '^(?!\\s).*(?<!\\s)$',
                },
              },
            },
          },
        },
        lawEnforcementAgency: {
          properties: {
            leContactAgency: {
              properties: {
                rawPartyFullName: {
                  title: 'LE contact agency',
                  type: 'string',
                  maxLength: 150,
                  pattern: '^(?!\\s).*(?<!\\s)$',
                },
              },
            },
            leContactName: {
              properties: {
                rawPartyFullName: {
                  title: 'LE contact name',
                  type: 'string',
                  maxLength: 150,
                  pattern: '^(?!\\s).*(?<!\\s)$',
                },
              },
            },
            leContactPhoneNumber: {
              type: 'object',
              properties: {
                phoneNumberText: {
                  title: 'LE contact phone number (include area code)',
                  type: 'string',
                  maxLength: 16,
                  // skipped not all same digit validation
                  pattern: '^[0-9]*$',
                },
                phoneNumberExtensionText: {
                  title: 'Ext.',
                  type: 'string',
                  maxLength: 6,
                  pattern: '^[0-9]*$',
                },
              },
            },
            leContactDate: {
              title: 'LE contact date',
              type: 'string',
              format: 'date',
            },
          },
        },
        dateFiled: {
          title: 'Date filed',
          description: 'Date filed will be auto-populated when the form is signed.',
          type: 'string',
          readOnly: true,
        },
      },
    },
    uiSchema: {
      filingInstitutionContactInformation: {
        financialInstitutionIdentification: {
          'ui:ObjectFieldTemplate': HorizontalLayoutObjectFieldTemplate,
        },
        typeOfFinancialInstitution: {
          organizationSubtypeId: {
            'ui:widget': 'checkboxes',
            'ui:options': {
              inline: true,
            },
          },
        },
        tin: {
          'ui:ObjectFieldTemplate': HorizontalLayoutObjectFieldTemplate,
        },
      },
    },
  },
  financialInstitution: {
    title: 'Financial Institution',
    schema: {
      description:
        'Please provide information about the financial institution and any branches where the activity occurred.',
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        title: 'Financial Institution Where Activity Occurred',
        properties: {
          typeOfFinancialInstitution: {
            type: 'object',
            properties: {
              organizationTypeId: {
                type: 'number',
                title: 'Type of financial institution',
                oneOf: [
                  { const: 1, title: 'Casino/Card Club' },
                  { const: 2, title: 'Depository Institution' },
                  { const: 3, title: 'Insurance company' },
                  { const: 4, title: 'MSB (Money Service Business)' },
                  { const: 5, title: 'Securities/Futures' },
                  { const: 11, title: 'Loan or Finance Company' },
                  { const: 12, title: 'Housing GSE (Government Sponsored Enterprise)' },
                  { const: 999, title: 'Other' },
                ],
              },
            },
            required: ['organizationTypeId'],
            allOf: [
              {
                if: {
                  $comment: 'if other is selected, show other text input',
                  properties: { organizationTypeId: { const: 999 } },
                  required: ['organizationTypeId'],
                },
                then: {
                  properties: {
                    otherOrganizationTypeText: {
                      type: 'string',
                      title: 'Please specify other',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                  },
                  required: ['otherOrganizationTypeText'],
                },
              },
              {
                if: {
                  $comment:
                    'if depository institution is selected, show related primary regulator type',
                  properties: {
                    organizationTypeId: { const: 2 },
                  },
                  required: ['organizationTypeId'],
                },
                then: {
                  properties: {
                    primaryRegulatorTypeCodeDepository: {
                      title: 'Primary federal regulator',
                      type: 'number',
                      oneOf: [
                        { const: 2, title: 'FDIC (Federal Deposit Insurance Corporation)' },
                        { const: 13, title: 'FHFA (Federal Housing Finance Agency)' },
                        { const: 7, title: 'IRS (Internal Revenue Service)' },
                        { const: 3, title: 'NCUA (National Credit Union Administration)' },
                        { const: 4, title: 'OCC (Office of the Comptroller of the Currency)' },
                      ],
                    },
                  },
                  required: ['primaryRegulatorTypeCodeDepository'],
                },
              },
              {
                if: {
                  $comment:
                    'if casino/card club, insurance company or MSB is selected, only show IRS as option',
                  properties: {
                    organizationTypeId: { anyOf: [{ const: 1 }, { const: 3 }, { const: 4 }] },
                  },
                  required: ['organizationTypeId'],
                },
                then: {
                  properties: {
                    primaryRegulatorTypeCodeIRS: {
                      title: 'Primary federal regulator',
                      type: 'number',
                      oneOf: [{ const: 7, title: 'IRS (Internal Revenue Service)' }],
                    },
                  },
                  required: ['primaryRegulatorTypeCodeIRS'],
                },
              },
              {
                if: {
                  $comment:
                    'if casino/card club, insurance company or MSB is selected, only show IRS as option',
                  properties: {
                    organizationTypeId: { anyOf: [{ const: 11 }, { const: 12 }, { const: 999 }] },
                  },
                  required: ['organizationTypeId'],
                },
                then: {
                  properties: {
                    primaryRegulatorTypeCode: {
                      title: 'Primary federal regulator',
                      type: 'number',
                      oneOf: [
                        { const: 9, title: 'CFTC (Commodities Futures Trading Commission)' },
                        { const: 1, title: 'Federal Reserve Board (FRB)' },
                        { const: 2, title: 'FDIC (Federal Deposit Insurance Corporation)' },
                        { const: 13, title: 'FHFA (Federal Housing Finance Agency)' },
                        { const: 7, title: 'IRS (Internal Revenue Service)' },
                        { const: 3, title: 'NCUA (National Credit Union Administration)' },
                        { const: 4, title: 'OCC (Office of the Comptroller of the Currency)' },
                        { const: 6, title: 'SEC (Securities and Exchange Commission)' },
                        { const: 99, title: 'Not Applicable' },
                      ],
                    },
                  },
                  required: ['primaryRegulatorTypeCode'],
                },
              },
              {
                if: {
                  $comment: 'if securities/futures is selected, show subtype selection',
                  properties: { organizationTypeId: { const: 5 } },
                  required: ['organizationTypeId'],
                },
                then: {
                  properties: {
                    primaryRegulatorTypeCodeSecuritiesAndFutures: {
                      title: 'Primary federal regulator',
                      type: 'number',
                      oneOf: [
                        { const: 9, title: 'CFTC (Commodities Futures Trading Commission)' },
                        { const: 6, title: 'SEC (Securities and Exchange Commission)' },
                      ],
                    },
                    organizationSubtypeIdSecuritiesAndFutures: {
                      title: 'Type of Securities and Futures Institution',
                      description: 'Select all that apply',
                      type: 'array',
                      uniqueItems: true,
                      items: {
                        type: 'number',
                        oneOf: FilingInstitutionOrganizationSubtypeCodes,
                      },
                      default: [],
                    },
                  },
                  required: [
                    'primaryRegulatorTypeCodeSecuritiesAndFutures',
                    'organizationSubtypeIdSecuritiesAndFutures',
                  ],
                  if: {
                    $comment: 'if other is selected, show other text input',
                    properties: {
                      organizationSubtypeIdSecuritiesAndFutures: { contains: { const: 5999 } },
                    },
                    required: ['organizationSubtypeIdSecuritiesAndFutures'],
                  },
                  then: {
                    properties: {
                      otherOrganizationSubTypeText: {
                        title: 'Other',
                        type: 'string',
                        pattern: '^(?!\\s).*(?<!\\s)$',
                      },
                    },
                    required: ['otherOrganizationSubTypeText'],
                  },
                },
              },
              {
                if: {
                  $comment: 'if casino card club is selected show subtype selection',
                  properties: { organizationTypeId: { const: 1 } },
                  required: ['organizationTypeId'],
                },
                then: {
                  properties: {
                    organizationSubtypeIdGaming: {
                      title: 'Type of gaming institution',
                      type: 'number',
                      oneOf: [
                        { const: 101, title: 'State licensed casino' },
                        { const: 102, title: 'Tribal authorized casino' },
                        { const: 103, title: 'Card club' },
                        { const: 1999, title: 'Other' },
                      ],
                    },
                  },
                  required: ['organizationSubtypeIdGaming'],
                  if: {
                    $comment: 'if other is selected, show other text input',
                    properties: { organizationSubtypeIdGaming: { const: 1999 } },
                    required: ['organizationSubtypeIdGaming'],
                  },
                  then: {
                    properties: {
                      otherOrganizationSubTypeText: {
                        title: 'Other',
                        type: 'string',
                        pattern: '^(?!\\s).*(?<!\\s)$',
                      },
                    },
                    required: ['otherOrganizationSubTypeText'],
                  },
                },
              },
            ],
          },
          financialInstitutionIdentification: {
            title: 'Financial Institution Identification',
            type: 'object',
            properties: {
              partyIdentificationTypeCode: {
                title: 'Type',
                type: 'number',
                oneOf: FilingInstitutionNonTinIdentificationTypeCode,
              },
              partyIdentificationNumberText: {
                title: 'Number',
                type: 'string',
                maxLength: 10,
                pattern: '^[A-Za-z0-9]*$',
              },
            },
            if: {
              properties: {
                partyIdentificationTypeCode: {
                  oneOf: FilingInstitutionNonTinIdentificationTypeCode,
                },
              },
              required: ['partyIdentificationTypeCode'],
            },
            then: {
              required: ['partyIdentificationNumberText'],
            },
          },
          financialInstitutionsRoleInTransaction: {
            title: 'Role in transaction',
            type: 'array', // this isn't an official enum in the finCEN schema
            uniqueItems: true,
            items: {
              type: 'string',
              enum: ['Selling location', 'Paying location'],
            },
          },
          legalNameOfFinancialInstitution: {
            properties: {
              rawPartyFullName: {
                title: 'Legal name of financial institution',
                type: 'string',
                maxLength: 150,
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
          },
          alternateName: {
            properties: {
              rawPartyFullName: {
                title: 'Alternate name',
                type: 'string',
                maxLength: 150,
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
          },
          tin: TIN,
          mailingAddress: MAILING_ADDRESS,
          internalControlNumber: {
            properties: {
              partyIdentificationNumberText: {
                title: 'Internal control number',
                type: 'string',
                maxLength: 20,
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
          },
          lossToFinancialAmountText: {
            title: 'Loss to Financial Institution',
            type: 'number',
            minimum: 1,
            maxLength: 15,
          },
          branches: {
            type: 'array',
            title: 'Branches',
            description:
              'Please detail the branch or branches where the suspicious activity occurred.',
            items: {
              type: 'object',
              properties: {
                roleInTransaction: {
                  title: 'Role in transaction',
                  type: 'array', // this isn't an official enum in the finCEN schema
                  uniqueItems: true,
                  items: {
                    type: 'string',
                    enum: ['Selling location', 'Paying location'],
                  },
                },
                address: MAILING_ADDRESS,
                rssdNumber: {
                  type: 'object',
                  properties: {
                    partyIdentificationNumberText: {
                      title: 'RSSD Number',
                      type: 'string',
                      maxLength: 20,
                      pattern: '^[0-9]*$',
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    uiSchema: {
      financialInstitutionWhereActivityOccurred: {
        typeOfFinancialInstitution: {
          organizationSubtypeId: {
            'ui:widget': 'checkboxes',
            'ui:options': {
              inline: true,
            },
          },
        },
        financialInstitutionsRoleInTransaction: {
          'ui:widget': 'radio',
        },
      },
    },
  },
  subjectInformation: {
    title: 'Subject Information',
    schema: {
      description: 'Please provide the information for each subject associated with this filing.',
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        properties: {
          subjectInformation: {
            title: 'Subject details',
            type: 'object',
            properties: {
              subjectBasicInformation: {
                properties: {
                  subjectType: {
                    type: 'string',
                    title: 'Type of subject',
                    default: 'Individual',
                    enum: ['Individual', 'Entity'],
                  },
                  roleInSuspiciousActivity: {
                    type: 'array',
                    title: 'Role in suspicious activity',
                    uniqueItems: true,
                    items: {
                      type: 'string',
                      enum: ['Purchaser/ Sender', 'Payee/ Receiver'],
                    },
                  },
                },
                if: { properties: { subjectType: { const: 'Entity' } } },
                then: {
                  $comment: 'Subject is an entity',
                  properties: {
                    rawPartyFullName: {
                      type: 'string',
                      title: 'Legal name',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                  },
                },
                else: {
                  $comment: 'Subject is an individual',
                  properties: {
                    rawIndividualFirstName: {
                      type: 'string',
                      title: 'First name',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                    rawEntityIndividualLastName: {
                      type: 'string',
                      title: 'Last name',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                    rawIndividualMiddleName: {
                      type: 'string',
                      title: 'Middle name/ Initial',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                    rawIndividualNameSuffixText: {
                      type: 'string',
                      title: 'Suffix',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                    gender: {
                      type: 'string',
                      title: 'Gender',
                      enum: ['Male', 'Female', 'Unknown'],
                    },
                    individualBirthDateText: {
                      type: 'string',
                      format: 'date',
                      title: 'Date of birth',
                    },
                    corroborativeStatement: {
                      type: 'string',
                      title: 'Corroborative statement',
                      oneOf: [
                        {
                          const: 'true',
                          title: 'A corroborative statement to the filer has been made.',
                        },
                        { const: 'false', title: 'No corroborative statement has been made.' },
                      ],
                    },
                  },
                },
              },
              alternateNames: {
                type: 'array',
                title: 'Alternate names',
                maxItems: 99,
                items: {
                  type: 'object',
                  properties: {
                    rawPartyFullName: {
                      title: 'Alternate name',
                      type: 'string',
                      maxLength: 150,
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                  },
                  required: ['rawPartyFullName'],
                },
              },
              occupationBusinessText: {
                type: 'string',
                title: 'Occupation or type of business',
                maxLength: 50,
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
              NAICSCode: {
                type: 'number',
                title: 'NAICS Code',
                oneOf: NAICSCode,
              },
              tin: {
                properties: {
                  partyIdentificationTypeCode: {
                    title: 'TIN Type',
                    type: 'number',
                    oneOf: [
                      { const: 2, title: 'EIN' },
                      { const: 1, title: 'SSN-ITIN' },
                      { const: 9, title: 'Foreign' },
                    ],
                  },
                },
                // required: ['partyIdentificationTypeCode'],
                allOf: [
                  {
                    if: {
                      properties: {
                        partyIdentificationTypeCode: { oneOf: [{ const: 1 }, { const: 2 }] },
                      },
                      required: ['partyIdentificationTypeCode'],
                    },
                    then: {
                      properties: {
                        partyIdentificationNumber: {
                          title: 'TIN',
                          type: 'string',
                          maxLength: 9,
                          minLength: 9,
                          pattern: '^[0-9]*$',
                        },
                      },
                      // required: ['partyIdentificationNumber'],
                    },
                  },
                  {
                    if: {
                      properties: {
                        partyIdentificationTypeCode: { oneOf: [{ const: 9 }] },
                      },
                      required: ['partyIdentificationTypeCode'],
                    },
                    then: {
                      properties: {
                        partyIdentificationNumber: {
                          title: 'TIN',
                          type: 'string',
                          maxLength: 25,
                          pattern: '^[A-Za-z0-9]*$',
                        },
                      },
                      // required: ['partyIdentificationNumber'],
                    },
                  },
                ],
              },
              phoneNumber: {
                type: 'array',
                title: 'Phone numbers',
                items: {
                  type: 'object',
                  properties: {
                    phoneNumberTypeCode: {
                      type: 'string',
                      title: 'Type',
                      oneOf: [
                        { const: 'R', title: 'Residence(Home)' },
                        { const: 'W', title: 'Work' },
                        { const: 'M', title: 'Mobile' },
                        { const: 'F', title: 'Fax' },
                      ],
                    },
                    phoneNumberText: {
                      type: 'string',
                      title: 'Phone number (include area code)',
                      pattern: '^[0-9]*$',
                      maxLength: 16, // not all same digits
                    },
                    phoneNumberExtensionText: {
                      type: 'string',
                      title: 'Ext.',
                      maxLength: 6,
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                  },
                  required: ['phoneNumberText'],
                },
              },
              emailAddress: {
                type: 'array',
                title: 'E-mail address',
                items: {
                  type: 'object',
                  properties: {
                    electronicAddressText: {
                      type: 'string',
                      title: 'E-mail address',
                      maxLength: 50,
                      format: 'email',
                    },
                  },
                },
                required: ['electronicAddressText'],
              },
              urlAddress: {
                type: 'array',
                title: 'Website address',
                description: 'e.g. https://www.example.com',
                items: {
                  type: 'object',
                  properties: {
                    electronicAddressText: {
                      type: 'string',
                      maxLength: 517,
                      format: 'uri',
                      title: 'Website (URL) address',
                    },
                  },
                  required: ['electronicAddressText'],
                },
              },
              addressInformation: {
                type: 'array',
                title: 'Subject Address Information',
                items: MAILING_ADDRESS,
              },
              formOfIdentification: {
                type: 'array',
                title: 'Identification document',
                description:
                  'Subjects can be identified with government IDs such as a driver license, passport, or an alien registration card.',
                items: {
                  type: 'object',
                  properties: {
                    partyIdentificationType: {
                      type: 'object',
                      properties: {
                        partyIdentificationTypeCode: {
                          type: 'number',
                          title: 'Identification type',
                          oneOf: [
                            { const: 5, title: "Driver's license/State ID" },
                            { const: 6, title: 'Passport' },
                            { const: 7, title: 'Alien registration' },
                            { const: 999, title: 'Other' },
                          ],
                        },
                      },
                      required: ['partyIdentificationTypeCode'],
                      if: {
                        properties: { partyIdentificationTypeCode: { const: 999 } },
                        required: ['partyIdentificationTypeCode'],
                      },
                      then: {
                        properties: {
                          otherPartyIdentificationTypeText: {
                            type: 'string',
                            title: 'Other identification type',
                            pattern: '^(?!\\s).*(?<!\\s)$',
                          },
                        },
                        required: ['otherPartyIdentificationTypeText'],
                      },
                    },
                    partyIdentificationNumberText: {
                      type: 'string',
                      title: 'Identification number',
                      maxLength: 24,
                      pattern: '^[A-Za-z0-9/*1*/]*$', // asterisks within the identification number are accepted for the subject’s form of identification
                    },
                    otherIssuerCountryText: {
                      type: 'string',
                      title: 'Country',
                      oneOf: ISO_3166_COUNTRY_CODES,
                    },
                  },
                  required: ['partyIdentificationType'],
                  allOf: [
                    {
                      if: {
                        properties: { otherIssuerCountryText: { const: 'US' } },
                        required: ['otherIssuerCountryText'],
                      },
                      then: {
                        properties: {
                          otherIssuerUSStateText: {
                            type: 'string',
                            title: 'Issuing state',
                            oneOf: USPS_STATE_CODES,
                          },
                        },
                        // required: ['otherIssuerUSStateText'],
                      },
                    },
                    {
                      if: {
                        properties: { otherIssuerCountryText: { const: 'CA' } },
                        required: ['otherIssuerCountryText'],
                      },
                      then: {
                        properties: {
                          otherIssuerCAStateText: {
                            type: 'string',
                            title: 'Issuing state',
                            oneOf: CANADA_POST_PROVINCE_CODES,
                          },
                        },
                        // required: ['otherIssuerCAStateText'],
                      },
                    },
                    {
                      if: {
                        properties: { otherIssuerCountryText: { const: 'MX' } },
                        required: ['otherIssuerCountryText'],
                      },
                      then: {
                        properties: {
                          otherIssuerMXStateText: {
                            type: 'string',
                            title: 'Issuing state',
                            oneOf: ISO_3166_2_MX_STATE_CODES,
                          },
                        },
                        // required: ['otherIssuerMXStateText'],
                      },
                    },
                  ],
                },
              },
              subjectRelationshipsToFinancialInstitution: {
                title: 'Relationship to financial institution',
                description:
                  'Relationship of the subject to a financial institution associated with this report',
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    subjectRelationshipFinancialInstitutionTINText: {
                      // NOTE: it must be one of the TIN value from previous section - find a way to reference another field at application level
                      type: 'string',
                      title: 'Institution TIN',
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                    subjectRelationshipTypes: {
                      type: 'array',
                      $comment: 'Empty array means no relationship to institution',
                      title: 'Relationship type',
                      default: [] as string[],
                      uniqueItems: true,
                      items: {
                        type: 'string',
                        enum: [
                          'Accountant',
                          'Agent',
                          'Appraiser',
                          'Attorney',
                          'Borrower',
                          'Customer',
                          'Director',
                          'Employee',
                          'Officer',
                          'Owner or Controlling Shareholder',
                          'Other',
                        ],
                      },
                    },
                  },
                  allOf: [
                    {
                      if: {
                        properties: { subjectRelationshipTypes: { contains: { const: 'Other' } } },
                      },
                      then: {
                        properties: {
                          otherPartyAssociationTypeText: {
                            type: 'string',
                            title: 'Other relationship',
                            pattern: '^(?!\\s).*(?<!\\s)$',
                          },
                        },
                        required: ['otherPartyAssociationTypeText'],
                      },
                    },
                    {
                      if: {
                        properties: {
                          subjectRelationshipTypes: {
                            contains: {
                              anyOf: [
                                { const: 'Director' },
                                { const: 'Employee' },
                                { const: 'Officer' },
                                { const: 'Owner or Controlling Shareholder' },
                              ],
                            },
                          },
                        },
                      },
                      then: {
                        properties: {
                          subjectRelationshipStatus: {
                            type: 'string',
                            title: 'Status of relationship',
                            enum: ['Relationship continues', 'Terminated', 'Suspended', 'Resigned'],
                          },
                        },
                        required: ['subjectRelationshipStatus'],
                      },
                    },
                    {
                      if: {
                        properties: {
                          subjectRelationshipStatus: {
                            anyOf: [
                              { const: 'Terminated' },
                              { const: 'Suspended' },
                              { const: 'Resigned' },
                            ],
                          },
                        },
                        required: ['subjectRelationshipStatus'],
                      },
                      then: {
                        properties: {
                          actionTakenDateText: {
                            type: 'string',
                            title: 'Action date',
                            format: 'date',
                          },
                        },
                        required: ['actionTakenDateText'],
                      },
                    },
                  ],
                },
              },
              financialInstitutionTINAndAccountNumberAffected: {
                type: 'array',
                title: 'Accounts',
                description:
                  'Please list financial institution TIN and account number(s) affected that are related to subject.',
                items: {
                  type: 'object',
                  properties: {
                    subjectRelationshipFinancialInstitutionTINText: {
                      type: 'string',
                      title: 'Institution TIN',
                      maxLength: 25,
                      pattern: '^(?!\\s).*(?<!\\s)$',
                    },
                    nonUSFinancialInstitutionIndicator: {
                      type: 'boolean',
                      title: 'Non-US Financial Institution',
                    },
                    accounts: {
                      type: 'array',
                      title: 'Associated accounts',
                      items: {
                        type: 'object',
                        properties: {
                          accountNumberText: {
                            title: 'Account number',
                            type: 'string',
                            pattern: '^[0-9]*$',
                            maxLength: 40,
                          },
                          accountClosedIndicator: {
                            type: 'boolean',
                            title: 'Closed account',
                          },
                        },
                        required: ['accountNumberText'],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    uiSchema: {
      subjectInformation: {
        tin: {
          'ui:ObjectFieldTemplate': HorizontalLayoutObjectFieldTemplate,
        },
      },
    },
  },
  activityInformation: {
    title: 'Activity Information',
    schema: {
      type: 'object',
      properties: {
        suspiciousActivityInformation: {
          type: 'object',
          properties: {
            totalSuspiciousAmountText: {
              $comment:
                'must be an integer, maximum 15 digits - leave it blank when amount is unknown and put 0 when no amount is involved',
              type: 'integer',
              title: 'Amount involved in this report',
              minimum: 0,
              maximum: 999999999999999,
            },
            suspiciousActivityDate: {
              type: 'object',
              title: 'Date or date range of suspicious activity for this report',
              properties: {
                suspiciousActivityFromDateText: {
                  type: 'string',
                  title: 'From',
                  format: 'date',
                },
                suspiciousActivityToDateText: {
                  type: 'string',
                  title: 'To',
                  format: 'date',
                },
              },
              required: ['suspiciousActivityFromDateText'],
            },
            cumulativeTotalViolationAmountText: {
              $comment: 'could be zero, 1-15 digits, only required if report type is continuing',
              type: 'number',
              title:
                'Cumulative amount (only applicable when filing type is continuing activity report)',
            },
            structuring: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Structuring',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      {
                        const: 111,
                        title:
                          'Alters or cancels transaction to avoid BSA recordkeeping requirement',
                      },
                      {
                        const: 112,
                        title: 'Alters or cancels transaction to avoid CTR requirement',
                      },
                      {
                        const: 106,
                        title:
                          'Suspicious inquiry by customer regarding BSA reporting or recordkeeping requirements',
                      },
                      { const: 113, title: 'Transaction(s) below BSA recordkeeping threshold' },
                      { const: 114, title: 'Transaction(s) below CTR threshold' },
                      { const: 1999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 1999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other structuring',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            terroristFinancing: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Terrorist financing',
                  default: [],
                  uniqueItems: true,
                  items: {
                    type: 'number',
                    oneOf: [
                      {
                        const: 701,
                        title: 'Known or suspected terrorist/terrorist organization',
                      },
                      { const: 7999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 7999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other terrorist financing',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            fraud: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Fraud',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 320, title: 'ACH' },
                      { const: 322, title: 'Advance fee' },
                      { const: 321, title: 'Business loan' },
                      { const: 301, title: 'Check' },
                      { const: 304, title: 'Consumer loan' },
                      { const: 305, title: 'Credit/ Debit Card' },
                      { const: 323, title: 'Healthcare/Public or private health insurance' },
                      { const: 308, title: 'Mail' },
                      { const: 309, title: 'Mass-marketing' },
                      { const: 324, title: 'Ponzi scheme' },
                      { const: 310, title: 'Pyramid schema' },
                      { const: 325, title: 'Securities fraud' },
                      { const: 312, title: 'Wire transfer' },
                      { const: 3999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 3999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other fraud',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            gamingActivities: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  uniqueItems: true,
                  default: [],
                  title: 'Gaming activities',
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 1201, title: 'Chip walking' },
                      { const: 1202, title: 'Minimal gaming with large transactions' },
                      { const: 1203, title: 'Suspicious use of counter checks or markers' },
                      { const: 1204, title: 'Unknown source of chips' },
                      { const: 12999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 12999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other gaming activities',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            moneyLaundering: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  uniqueItems: true,
                  default: [],
                  title: 'Money laundering',
                  items: {
                    type: 'number',
                    oneOf: [
                      {
                        const: 801,
                        title: 'Exchanges small bills for large bills or vice versa',
                      },
                      { const: 824, title: 'Funnel account' },
                      {
                        const: 820,
                        title: 'Suspicion concerning the physical condition of funds',
                      },
                      { const: 821, title: 'Suspicion concerning the source of funds' },
                      {
                        const: 804,
                        title: 'Suspicious designation of beneficiaries, assignees or joint owners',
                      },
                      { const: 805, title: 'Suspicious EFT/wire transfers' },
                      { const: 822, title: 'Suspicious exchange of currencies' },
                      { const: 806, title: 'Suspicious receipt of government payments/benefits' },
                      { const: 807, title: 'Suspicious use of multiple accounts' },
                      { const: 808, title: 'Suspicious use of noncash monetary instruments' },
                      {
                        const: 809,
                        title: 'Suspicious use of third-party transactions (straw-man)',
                      },
                      {
                        const: 823,
                        title: 'Trade Based Money Laundering/Black Market Peso Exchange',
                      },
                      { const: 812, title: 'Transaction out of pattern for customer(s)' },
                      { const: 8999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 8999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other money laundering',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            identificationOrDocumentation: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Identification/ Documentation',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 401, title: 'Changes spelling or arrangement of name' },
                      {
                        const: 402,
                        title: 'Multiple individuals with same or similar identities',
                      },
                      { const: 403, title: 'Provided questionable or false documentation' },
                      { const: 409, title: 'Provided questionable or false identification' },
                      { const: 404, title: 'Refused or avoided request for documentation' },
                      { const: 405, title: 'Single individual with multiple identities' },
                      { const: 4999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 4999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other identification/ Documentation',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            otherSuspiciousActivities: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Other suspicious activities',
                  default: [],
                  uniqueItems: true,
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 920, title: 'Account takeover' },
                      { const: 901, title: 'Bribery or gratuity' },
                      { const: 917, title: 'Counterfeit Instrument (other)' },
                      { const: 921, title: 'Elder financial exploitation' },
                      { const: 903, title: 'Embezzlement/theft/disappearance of funds' },
                      { const: 904, title: 'Forgeries' },
                      { const: 926, title: 'Human smuggling' },
                      { const: 927, title: 'Human trafficking' },
                      { const: 905, title: 'Identity theft' },
                      {
                        const: 922,
                        title:
                          'Little or no concern for product performance penalties, fees, or tax consequences',
                      },
                      { const: 924, title: 'Misuse of position or self-dealing' },
                      { const: 907, title: 'Suspected public/private corruption (domestic)' },
                      { const: 908, title: 'Suspected public/private corruption (foreign)' },
                      { const: 909, title: 'Suspicious use of informal value transfer system' },
                      { const: 910, title: 'Suspicious use of multiple locations' },
                      {
                        const: 925,
                        title: 'Transaction with no apparent economic, business, or lawful purpose',
                      },
                      {
                        const: 928,
                        title: 'Transaction(s) involving foreign high risk jurisdiction',
                      },
                      { const: 911, title: 'Two or more individuals working together' },
                      { const: 913, title: 'Unlicensed or unregistered MSB' },
                      { const: 9999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 9999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    title: 'Other Suspicious Activities',
                    type: 'string',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            insurance: {
              $comment: 'not required',
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Insurance',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 501, title: 'Excessive insurance' },
                      {
                        const: 502,
                        title: 'Excessive or unusual cash borrowing against policy/annuity',
                      },
                      { const: 504, title: 'Proceeds sent to unrelated third party' },
                      {
                        const: 505,
                        title:
                          "Suspicious life settlement sales insurance (e.g., STOLI's, Viaticals)",
                      },
                      { const: 506, title: 'Suspicious termination of policy or contract' },
                      { const: 507, title: 'Unclear or no insurable interest' },
                      { const: 5999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 5999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    title: 'Other insurance',
                    type: 'string',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            securitiesOrFuturesOrOptions: {
              $comment: 'not required',
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Securities/ Futures/ Options',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 601, title: 'Insider trading' },
                      { const: 608, title: 'Market manipulation' },
                      { const: 603, title: 'Misappropriation' },
                      { const: 604, title: 'Unauthorized pooling' },
                      { const: 609, title: 'Wash trading' },
                      { const: 6999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 6999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other Securities/ Futures/ Options',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            mortgageFraud: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Mortgage fraud',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 1005, title: 'Application fraud' },
                      { const: 1001, title: 'Appraisal fraud' },
                      { const: 1006, title: 'Foreclosure/ Short sale fraud' },
                      { const: 1003, title: 'Loan Modification fraud' },
                      { const: 1007, title: 'Origination fraud' },
                      { const: 10999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 10999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other mortgage fraud',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            cyberEvent: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Cyber event',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 1101, title: 'Against financial institution(s)' },
                      { const: 1102, title: 'Against financial institution customer(s)' },
                      { const: 11999, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 11999 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other cyber event',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            products: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  uniqueItems: true,
                  default: [],
                  title: 'Products',
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 2, title: 'Bonds/Notes' },
                      { const: 3, title: 'Commercial mortgage' },
                      { const: 4, title: 'Commercial paper' },
                      { const: 5, title: 'Credit card' },
                      { const: 6, title: 'Debit card' },
                      { const: 46, title: 'Deposit Account' },
                      { const: 7, title: 'Forex transactions' },
                      { const: 8, title: 'Futures/Options on futures' },
                      { const: 9, title: 'Hedge fund' },
                      { const: 11, title: 'Home equity line of credit' },
                      { const: 10, title: 'Home equity loan' },
                      { const: 12, title: 'Insurance/Annuity products' },
                      { const: 47, title: 'Microcap securities' },
                      { const: 13, title: 'Mutual fund' },
                      { const: 14, title: 'Options on securities' },
                      { const: 16, title: 'Prepaid access' },
                      { const: 17, title: 'Residential mortgage' },
                      { const: 18, title: 'Security futures products' },
                      { const: 19, title: 'Stocks' },
                      { const: 20, title: 'Swap, hybrid, or other derivative' },
                      { const: 30, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 30 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other product type',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            instrumentOrPaymentMechanism: {
              type: 'object',
              properties: {
                suspiciousActivitySubtypeID: {
                  type: 'array',
                  title: 'Instrument/ Payment Mechanism',
                  uniqueItems: true,
                  default: [],
                  items: {
                    type: 'number',
                    oneOf: [
                      { const: 31, title: "Bank/Cashier's check" },
                      { const: 32, title: 'Foreign currency' },
                      { const: 33, title: 'Funds transfer' },
                      { const: 34, title: 'Gaming instruments' },
                      { const: 35, title: 'Government payment' },
                      { const: 36, title: 'Money orders' },
                      { const: 37, title: 'Personal/Business check' },
                      { const: 38, title: 'Travelers checks' },
                      { const: 39, title: 'U.S. Currency' },
                      { const: 41, title: 'Other' },
                    ],
                  },
                },
              },
              if: {
                properties: { suspiciousActivitySubtypeID: { contains: { const: 41 } } },
              },
              then: {
                properties: {
                  otherSuspiciousActivityTypeText: {
                    type: 'string',
                    title: 'Other Instrument/ Payment Mechanism',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                },
                required: ['otherSuspiciousActivityTypeText'],
              },
            },
            ipAddress: {
              type: 'array',
              title: 'IP address',
              items: {
                type: 'object',
                properties: {
                  IPAddressText: {
                    type: 'string',
                    title: 'IP address',
                    format: 'ipv4', // TODO: handle ipv6 validation
                  },
                  activityIPAddressDateText: {
                    type: 'string',
                    title: 'Date',
                    format: 'date',
                  },
                  activityIPAddressTimeStampText: {
                    type: 'string',
                    title: 'Timestamp(UTC)',
                    format: 'time',
                  },
                },
                required: ['IPAddressText'],
              },
            },
            cyberEventIndicators: {
              type: 'array',
              title: 'Cyber event indicators',
              items: {
                type: 'object',
                properties: {
                  cyberEventIndicatorsTypeCode: {
                    type: 'number',
                    title: 'Event type',
                    oneOf: [
                      { const: 1, title: 'Command and control IP address' },
                      { const: 2, title: 'Command and control URL/ domain' },
                      { const: 3, title: 'Malware MD5, Malware SHA-1, or Malware SHA-256' },
                      { const: 4, title: 'Media Access Control (MAC) Address' },
                      { const: 5, title: 'Port' },
                      { const: 6, title: 'Suspicious e-mail address' },
                      { const: 7, title: 'Suspicious file name' },
                      { const: 8, title: 'Suspicious IP address' },
                      { const: 9, title: 'Suspicious URL/ domain' },
                      { const: 10, title: 'Targeted system' },
                      { const: 999, title: 'Other' },
                    ],
                  },
                  eventValueText: {
                    type: 'string',
                    title: 'Event value',
                    pattern: '^(?!\\s).*(?<!\\s)$',
                  },
                  cyberEventDateText: {
                    type: 'string',
                    title: 'Date',
                    format: 'date',
                  },
                  cyberEventTimeStampText: {
                    type: 'string',
                    title: 'Timestamp(UTC)',
                    format: 'time',
                  },
                },
                allOf: [
                  {
                    if: {
                      properties: { cyberEventIndicatorsTypeCode: { const: 999 } },
                      required: ['cyberEventIndicatorsTypeCode'],
                    },
                    then: {
                      properties: {
                        cyberEventTypeOtherText: {
                          type: 'string',
                          title: 'Other event type', // TODO: need to specify UI order to render it after cyberEventType
                          pattern: '^(?!\\s).*(?<!\\s)$',
                        },
                      },
                      required: ['cyberEventTypeOtherText'],
                    },
                  },
                  {
                    if: {
                      properties: {
                        cyberEventIndicatorsTypeCode: {
                          anyOf: [{ const: 1 }, { const: 8 }],
                        },
                      },
                      required: ['cyberEventIndicatorsTypeCode'],
                    },
                    then: {
                      properties: {
                        eventValueText: {
                          type: 'string',
                          title: 'IP Address Value',
                          format: 'ipv4', // TODO: handle ipv6 address validation
                        },
                      },
                      required: ['eventValueText'],
                    },
                  },
                  {
                    if: {
                      properties: {
                        cyberEventIndicatorsTypeCode: {
                          anyOf: [{ const: 2 }, { const: 9 }],
                        },
                      },
                      required: ['cyberEventIndicatorsTypeCode'],
                    },
                    then: {
                      properties: {
                        eventValueText: {
                          type: 'string',
                          format: 'uri',
                        },
                      },
                      required: ['eventValueText'],
                    },
                  },
                  {
                    if: {
                      properties: { cyberEventIndicatorsTypeCode: { const: 4 } },
                      required: ['cyberEventIndicatorsTypeCode'],
                    },
                    then: {
                      properties: {
                        eventValueText: {
                          type: 'string',
                          title: 'MAC Address',
                          pattern: '^([A-F0-9]{2}:){5}[A-F0-9]{2}$',
                        },
                      },
                      required: ['eventValueText'],
                    },
                  },
                  {
                    if: {
                      properties: { cyberEventIndicatorsTypeCode: { const: 6 } },
                      required: ['cyberEventIndicatorsTypeCode'],
                    },
                    then: {
                      properties: {
                        eventValueText: {
                          type: 'string',
                          title: 'Email Address',
                          format: 'email',
                        },
                      },
                      required: ['eventValueText'],
                    },
                  },
                ],
              },
            },
            commodityType: {
              title: 'Commodity type',
              type: 'array',
              items: {
                type: 'string',
                title: 'Commodity type description',
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
            productOrInstrumentType: {
              title: 'Product/Instrument description',
              maxItems: 99,
              type: 'array',
              items: {
                type: 'string',
                maxLength: 50,
                title: 'Product/Instrument description',
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
            tradedMarket: {
              type: 'array',
              title: 'Market where traded',
              maxItems: 99,
              $comment:
                'Market trade code can be found at http://www.iso15022.org/MIC/homepageMIC.htm.',
              items: {
                type: 'string',
                title: 'Market trade code',
                minLength: 3,
                maxLength: 5,
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
            CUSIPNumber: {
              title: 'CUSIP number',
              maxItems: 99,
              type: 'array',
              items: {
                type: 'string',
                maxLength: 12,
                title: 'CUSIP number',
                pattern: '^(?!\\s).*(?<!\\s)$',
              },
            },
          },
        },
      },
    },
    uiSchema: {
      suspiciousActivityInformation: {
        suspiciousActivityDate: {
          'ui:ObjectFieldTemplate': HorizontalLayoutObjectFieldTemplate,
        },
      },
    },
  },
  narrative: {
    title: 'Narrative',
    schema: {
      type: 'object',
      properties: {
        narrativeText: {
          type: 'string',
          maxLength: 20000,
          pattern: '^[\x00-\x7F]*$',
        },
      },
      required: ['narrativeText'],
    },
    uiSchema: {
      narrativeText: {
        'ui:label': false,
        'ui:field': 'SARNarrativeField',
      },
    },
  },
};
